import { ArchiveX, ListX, X } from "lucide-react";
import { useState } from "react";

import type { Equipo, TipoBaja } from "@/lib/api";

/**
 * Al "eliminar" un equipo asignado se pregunta qué tipo de baja es:
 *  - lógica: solo se quita de la lista (captura errónea, reasignación).
 *  - física: el usuario entrega el equipo → queda en "Bajas de asignación"
 *    con formato PDF para firma.
 * El botón Confirmar se habilita hasta que el usuario elige una opción.
 */
export function BajaEquipoModal({
  equipo,
  nombre,
  pending,
  onCancel,
  onConfirm,
}: {
  equipo: Equipo;
  nombre: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (tipo: TipoBaja, motivo: string) => void;
}) {
  const [tipo, setTipo] = useState<TipoBaja | null>(null);
  const [motivo, setMotivo] = useState("");

  const opciones: {
    valor: TipoBaja;
    titulo: string;
    detalle: string;
    Icono: typeof ListX;
  }[] = [
    {
      valor: "logica",
      titulo: "Baja lógica",
      detalle:
        "Solo se quita de la lista del usuario. No hay entrega física del equipo (captura errónea, reasignación, corrección). Queda registrada, pero solo la ve el administrador y no genera PDF.",
      Icono: ListX,
    },
    {
      valor: "fisica",
      titulo: "Baja física",
      detalle:
        "El usuario entrega o devuelve el equipo. Se registra en “Bajas de asignación” y se genera el formato PDF para firma.",
      Icono: ArchiveX,
    },
  ];

  const descripcion = [equipo.marca, equipo.modelo].filter(Boolean).join(" ");

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-900/50 backdrop-blur-[2px] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="baja-titulo"
      onKeyDown={(e) => {
        if (e.key === "Escape" && !pending) onCancel();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="baja-titulo" className="text-base font-semibold text-slate-900">
              Dar de baja {equipo.tipo} de {nombre}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {descripcion || "Sin marca/modelo"}
              {equipo.numero_serie && (
                <>
                  {" "}
                  · Serie <span className="tabular-nums">{equipo.numero_serie}</span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="text-slate-400 hover:text-slate-700 p-1 disabled:opacity-50"
            title="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div>
          <div className="text-sm font-medium text-slate-700 mb-2">¿Qué tipo de baja es?</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {opciones.map(({ valor, titulo, detalle, Icono }) => {
              const activo = tipo === valor;
              return (
                <label
                  key={valor}
                  className={`cursor-pointer rounded-lg border p-3 flex gap-3 transition ${
                    activo
                      ? valor === "fisica"
                        ? "border-red-500 ring-2 ring-red-200 bg-red-50"
                        : "border-blue-500 ring-2 ring-blue-200 bg-blue-50"
                      : "border-slate-200 hover:border-slate-400"
                  }`}
                >
                  <input
                    type="radio"
                    name="tipo-baja"
                    className="sr-only"
                    value={valor}
                    checked={activo}
                    onChange={() => setTipo(valor)}
                  />
                  <Icono
                    className={`w-5 h-5 shrink-0 mt-0.5 ${
                      activo
                        ? valor === "fisica"
                          ? "text-red-600"
                          : "text-blue-600"
                        : "text-slate-400"
                    }`}
                  />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{titulo}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{detalle}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">
            Motivo / observaciones{" "}
            <span className="font-normal text-slate-400">
              (opcional{tipo === "fisica" ? "; aparece en el formato PDF" : ""})
            </span>
          </span>
          <textarea
            className="mt-1 border rounded px-2 py-1.5 text-sm block w-full"
            rows={2}
            maxLength={300}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder={
              tipo === "fisica"
                ? "Ej. renuncia; se entrega con cargador, rayón en la tapa…"
                : "Ej. se capturó por error; se reasignó a otro usuario…"
            }
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-2 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!tipo || pending}
            onClick={() => tipo && onConfirm(tipo, motivo)}
            className={`inline-flex items-center gap-2 text-white rounded px-4 py-2 text-sm disabled:opacity-50 ${
              tipo === "fisica"
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {pending
              ? "Registrando…"
              : tipo === "fisica"
                ? "Registrar baja física"
                : tipo === "logica"
                  ? "Quitar de la lista"
                  : "Confirmar baja"}
          </button>
        </div>
      </div>
    </div>
  );
}
